import type { DrawerProps } from 'antd';
import { Button, Dropdown, Flex, Grid } from 'antd';
import type { BaseButtonProps } from 'antd/es/button/button';
import type { ReactNode } from 'react';

import AddButton from '@/components/AddButton';

import * as Styled from './styles';

const { useBreakpoint } = Grid;

/** `onClose` is omitted: closing runs through `onCancel`. */
export type Props = Omit<DrawerProps, 'onClose'> & {
  actionButton?: {
    text: string;
    type?: NonNullable<BaseButtonProps['type']>;
    icon?: NonNullable<BaseButtonProps['icon']>;
    onClick?: VoidFunction;
    /** Renders the trigger as the standard `AddButton`. Use for add actions. */
    isAddAction?: boolean;
    /** Filled treatment for a screen-specific prominent add action. */
    appearance?: 'default' | 'filled';
  };
  okText?: string;
  onOk?: VoidFunction;
  onCancel?: VoidFunction;
  disabled?: boolean;
  /**
   * Disables the confirm button alone, leaving Cancel reachable. Use this — not
   * `disabled` — to hold Save back until a form is valid, so the user is never
   * trapped in the drawer.
   */
  okDisabled?: boolean;
  /**
   * Marks a mutation as in flight: the footer buttons go into their loading /
   * disabled state and every close path is sealed — close icon, Esc and mask —
   * so the body cannot unmount while the request is still running.
   */
  loading?: boolean;
  showFooter?: boolean;
  moreActions?: ReactNode;
  moreActionOptions?: Array<{
    label: string;
    danger?: boolean;
    onClick: VoidFunction;
  }>;
};

const Drawer = ({
  actionButton,
  okText = 'Save',
  onOk,
  onCancel,
  loading,
  disabled,
  okDisabled,
  showFooter = true,
  children,
  moreActions,
  moreActionOptions,
  ...props
}: Props) => {
  const screens = useBreakpoint();
  const drawerWidth = screens.lg ? '30vw' : undefined;

  // antd supplies an argument to each of these, and a `VoidFunction` may still
  // declare an optional first parameter — so all are invoked with none.
  const handleCancel = () => onCancel?.();
  const handleOk = () => onOk?.();
  const handleActionButtonClick = () => actionButton?.onClick?.();

  // Sealed while a mutation runs so no close path can unmount the body before
  // the request settles. Spread after `props` so it overrides what a consumer
  // sets, and leaves those values untouched once loading clears.
  const closePathGuard = loading
    ? {
        closable: false,
        keyboard: false,
        maskClosable: false,
        onClose: undefined
      }
    : undefined;

  return (
    <>
      {actionButton &&
        (actionButton.isAddAction ? (
          <AddButton
            appearance={actionButton.appearance}
            icon={actionButton.icon}
            onClick={handleActionButtonClick}>
            {actionButton.text}
          </AddButton>
        ) : (
          <Button
            icon={actionButton.icon}
            type={actionButton.type ?? 'primary'}
            onClick={handleActionButtonClick}>
            {actionButton.text}
          </Button>
        ))}
      {/* The loading seal is spread last so it can withdraw every close path. */}
      <Styled.Drawer
        width={drawerWidth}
        {...props}
        onClose={handleCancel}
        {...closePathGuard}>
        <Styled.FullHeightContainer>
          <Styled.Body flex={1} vertical>
            {children}

            {showFooter && (
              <Styled.FooterWrapper>
                <Button
                  disabled={loading || disabled}
                  onClick={handleCancel}
                  type='text'>
                  Cancel
                </Button>
                <Flex gap={8}>
                  {moreActions}
                  {!!moreActionOptions?.length && (
                    <Dropdown.Button
                      menu={{
                        items: moreActionOptions.map(opt => ({
                          key: opt.label,
                          label: opt.label,
                          danger: opt.danger,
                          onClick: () => opt.onClick()
                        }))
                      }}>
                      More actions
                    </Dropdown.Button>
                  )}

                  <Button
                    loading={loading}
                    disabled={loading || disabled || okDisabled}
                    onClick={handleOk}
                    type='primary'>
                    {okText}
                  </Button>
                </Flex>
              </Styled.FooterWrapper>
            )}
          </Styled.Body>
        </Styled.FullHeightContainer>
      </Styled.Drawer>
    </>
  );
};

Drawer.Footer = Styled.FooterWrapper;

export default Drawer;
